/**
 * AuthContext.js
 * Path: frontend/src/context/AuthContext.js
 * Description: Globalny kontekst sesji użytkownika.
 * Related: hooks/useAuth.js, api/auth.js
 */

import { createContext, useEffect, useState } from "react";
import { fetchProfile } from "../api/auth";
import { SESSION_INVALIDATED_EVENT } from "../api/apiBase";

export const AuthContext = createContext({
  user: null,
  token: null,
  hydrated: false,
  setUser: () => {},
  setToken: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    function clearInvalidatedSession() {
      setUser(null);
      setToken(null);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    }

    window.addEventListener(SESSION_INVALIDATED_EVENT, clearInvalidatedSession);
    return () => {
      window.removeEventListener(SESSION_INVALIDATED_EVENT, clearInvalidatedSession);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (!storedToken) {
        if (!cancelled) setHydrated(true);
        return;
      }

      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          if (!cancelled) setUser(parsed);
        } catch (_) {
          localStorage.removeItem("user");
        }
      }

      if (!cancelled) {
        setToken(storedToken);
      }

      try {
        const profile = await fetchProfile(storedToken);
        if (!cancelled) {
          setUser(profile.user || null);
          if (profile.user) {
            localStorage.setItem("user", JSON.stringify(profile.user));
          }
        }
      } catch (_) {
        if (!cancelled) {
          setUser(null);
          setToken(null);
          localStorage.removeItem("user");
          localStorage.removeItem("token");
        }
      } finally {
        if (!cancelled) setHydrated(true);
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (token && user) {
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      return;
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }, [token, user]);

  return (
    <AuthContext.Provider value={{ user, token, hydrated, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
}
